import type { RelatedIssue, ServicesWithActiveIncidents } from '../../common/types.tsx';
import type { JiraSearchApiIssue } from './types.tsx';

export const CF_SEPARATOR = 'customfield_';

export const emptyServicesWithActiveIncidentsObject = (
	serviceids: string[],
): ServicesWithActiveIncidents => {
	const serviceIdIssues: ServicesWithActiveIncidents = {};
	return serviceids.reduce(
		(accServiceIdIssues, serviceId) => ({
			[serviceId]: [],
			// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
			...accServiceIdIssues,
		}),
		serviceIdIssues,
	);
};

export const mapJiraSearchApiIssueToRelatedRequestedIssue = (
	issue: JiraSearchApiIssue,
	affectedServicesFieldId: string,
	plannedStartTimeFieldId?: string,
	plannedEndTimeFieldId?: string,
): RelatedIssue => {
	const issueUrl = `/browse/${issue.key}`;

	return {
		id: issue.id,
		issueKey: issue.key,
		issueSummary: issue.fields.summary,
		issueTypeIconUrl: issue.fields.issuetype.iconUrl,
		issueLink: issueUrl,
		issueTypeName: issue.fields.issuetype.name,
		issuePriorityUrl: issue.fields.priority?.iconUrl,
		issuePriorityName: issue.fields.priority?.name,
		statusCategory: issue.fields.status.statusCategory.name,
		statusCategoryId: issue.fields.status.statusCategory.id,
		statusId: issue.fields.status.id,
		statusName: issue.fields.status.name,
		assigneeUrl: issue.fields.assignee?.avatarUrls['48x48'],
		assigneeDisplayName: issue.fields.assignee?.displayName,
		created: Date.parse(issue.fields.created),
		isResolved: !!issue.fields.resolution,
		resolutiondate:
			issue.fields.resolutiondate !== undefined
				? Date.parse(issue.fields.resolutiondate)
				: undefined,
		services: issue.renderedFields ? issue.renderedFields[affectedServicesFieldId] : [],
		plannedStartTime:
			typeof plannedStartTimeFieldId === 'string' && issue.fields
				? issue.fields[plannedStartTimeFieldId]
				: undefined,
		plannedEndTime:
			typeof plannedEndTimeFieldId === 'string' && issue.fields
				? issue.fields[plannedEndTimeFieldId]
				: undefined,
	};
};

export const mapIssuesToServicesWithActiveIncidents = (
	issues: JiraSearchApiIssue[],
	serviceIds: string[],
	affectedServicesFieldId: string,
): ServicesWithActiveIncidents =>
	issues.reduce((accServiceIdIssues, issue: JiraSearchApiIssue) => {
		// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
		const currentServiceIdIssues = { ...accServiceIdIssues };
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		issue.fields[affectedServicesFieldId]?.forEach((issueAssociatedService: any) => {
			if (currentServiceIdIssues[issueAssociatedService.id]) {
				currentServiceIdIssues[issueAssociatedService.id].push(
					mapJiraSearchApiIssueToRelatedRequestedIssue(issue, affectedServicesFieldId),
				);
			}
		});
		return currentServiceIdIssues;
	}, emptyServicesWithActiveIncidentsObject(serviceIds));

export const getFieldNameForJql = (fieldName: string) => {
	// custom fields are indexed by their ids and only work with JQL when referenced as such `cf[id]`.
	const customFieldId = fieldName.split(CF_SEPARATOR)[1];
	return `cf[${customFieldId}]`;
};
